import { Transform, Type } from 'class-transformer';
import dayjs from 'dayjs';
import { BaseType, Model } from './model';
import { Integration } from './integration';
import { LinkedModel } from './linked_model';
import { TransformStringToDayJS } from './utils';
import { camelCase } from 'change-case';

export enum LinkedIntegrationHealthStatus {
  INCOMPLETE = 'incomplete',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  RELINKED_REQUIRED = 'relink_required',
  UNHEALTHY = 'unhealthy',
  HEALTHY = 'healthy',
}

export type LinkedIntegrationHealthStatusReason =
  | 'invalid_credentials'
  | 'missing_permission'
  | 'missing_settings';

export class LinkedIntegrationHealth {
  status: LinkedIntegrationHealthStatus;

  details: Record<string, any> | null;

  message: string | null;

  reason: LinkedIntegrationHealthStatusReason | null;
}

export class LinkedIntegration extends Model {
  id: string;

  api_key: string | null;

  api_subdomain: string | null;

  @Type(() => LinkedIntegrationHealth)
  health: LinkedIntegrationHealth;

  is_configured: boolean;

  is_healthy: boolean;

  is_running_setup: boolean;

  is_webhooks_enabled: boolean;

  @Type(() => Integration)
  integration: Integration;

  integration_name: string;

  @TransformKeysToCamel()
  integration_connection?: Record<string, any> | null;

  integration_connection_id: string | null;

  @TransformStringToDayJS()
  last_data_sync: Date | null;

  @Type(() => LinkedModel)
  models: LinkedModel[];

  @TransformStringToDayJS()
  next_sync_at: dayjs.Dayjs;

  organization_id: string;

  requires_setup: boolean;

  settings: Record<string, any>;

  username: string | null;

  webhook_listener_url: string | null;

  webhook_setup_error: string | null;

  last_sync_error: string | null;
}

export class LinkedIntegrationAPIUsageMetric extends BaseType {
  @TransformStringToDayJS()
  timestamp: dayjs.Dayjs;

  // average payload size in bytes
  avg_payload_size: number = 0;

  // average time in seconds
  avg_response_time: number = 0;

  interval: string;

  total_success: number = 0;

  total_error: number = 0;

  total_requests: number = 0;
}

function TransformKeysToCamel() {
  return Transform(({ value, type, key }) => keysToCamel(value));
}

const keysToCamel = (obj: any) => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((i) => keysToCamel(i));
  }

  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[camelCase(key)] = keysToCamel(obj[key]);
    }
  }

  return newObj;
};
